import React, { useEffect, useRef } from "react";

import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { ErrorMessage } from "src/shell/components/ErrorMessage";

import type {
  ConfigValidations,
  FailedStep,
} from "../../hooks/useStackValidation";

interface StackStatusBarProps {
  actionError: string | null;
  onDismissError: () => void;
  deployMessage: string | null;
  onDismissDeployMessage: () => void;
  isProvisioningFailed: boolean;
  isFailed: boolean;
  failedStep: FailedStep | null;
  isProvisioning: boolean;
  isCiControlled: boolean;
  isCiManaged: boolean;
  configReady: boolean;
  orgBillingLoading: boolean;
  configValidations: ConfigValidations;
  isAdopted?: boolean;
  onRetryProvisioning: () => void;
  retryProvisioningLoading: boolean;
  onEdit?: () => void;
}

type AlertKind =
  | "actionError"
  | "failed"
  | "provisioningFailed"
  | "configWarning"
  | "provisioning"
  | "success"
  | "ciInfo"
  | "adoptedInfo";

function resolveAlertKind(props: StackStatusBarProps): AlertKind | null {
  if (props.actionError) return "actionError";
  if (props.isFailed) return "failed";
  if (props.isProvisioningFailed) return "provisioningFailed";
  if (!props.configReady && !props.isProvisioning && !props.orgBillingLoading)
    return "configWarning";
  if (props.isProvisioning) return "provisioning";
  if (props.deployMessage) return "success";
  if (props.isCiControlled) return "ciInfo";
  if (props.isAdopted && props.onEdit) return "adoptedInfo";
  return null;
}

// ── Thin banner styles per severity ───────────────────────────────────────────

const BANNER_STYLES: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  warning: {
    bg: "rgba(245,158,11,0.06)",
    border: "1px solid rgba(245,158,11,0.2)",
    text: "#f59e0b",
  },
  error: {
    bg: "rgba(239,68,68,0.06)",
    border: "1px solid rgba(239,68,68,0.2)",
    text: "#ef4444",
  },
  info: {
    bg: "rgba(59,130,246,0.06)",
    border: "1px solid rgba(59,130,246,0.2)",
    text: "#60a5fa",
  },
  success: {
    bg: "rgba(16,185,129,0.06)",
    border: "1px solid rgba(16,185,129,0.2)",
    text: "#10b981",
  },
};

function ThinBanner({
  severity,
  children,
  action,
}: {
  severity: "warning" | "error" | "info" | "success";
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const s = BANNER_STYLES[severity];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 36,
        px: 2,
        mb: 2,
        borderRadius: 1.5,
        bgcolor: s.bg,
        border: s.border,
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: s.text,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Typography>
      {action}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function StackStatusBar(props: StackStatusBarProps) {
  const kind = resolveAlertKind(props);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (kind === "success" && props.deployMessage) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        props.onDismissDeployMessage();
      }, 5_000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [kind, props.deployMessage, props.onDismissDeployMessage]);

  if (!kind) return null;

  if (kind === "actionError") {
    return (
      <ErrorMessage
        message={props.actionError!}
        sx={{ mb: 2 }}
        onClose={props.onDismissError}
      />
    );
  }

  if (kind === "failed") {
    return (
      <ThinBanner severity="error">
        {props.failedStep
          ? `${props.failedStep.phase} failed — ${props.failedStep.message}. Check the Pipeline tab, then retry.`
          : "Last deployment failed — check the Pipeline tab for error details."}
      </ThinBanner>
    );
  }

  if (kind === "provisioningFailed") {
    return (
      <ThinBanner
        severity="warning"
        action={
          <Button
            size="small"
            sx={{ color: "#f59e0b", textTransform: "none", minWidth: "auto" }}
            startIcon={
              props.retryProvisioningLoading ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon fontSize="small" />
              )
            }
            onClick={props.onRetryProvisioning}
            disabled={props.retryProvisioningLoading}
          >
            Retry
          </Button>
        }
      >
        VCS provisioning failed — connect a GitHub org via VCS Connections, then
        click Retry.
      </ThinBanner>
    );
  }

  if (kind === "configWarning") {
    const blockedCount = Object.values(props.configValidations).filter(
      (v) => !v,
    ).length;
    const hasConfigIssue = !props.configValidations.hasConfig;
    const editLabel = props.isAdopted
      ? "Review detected services →"
      : "Configure stack →";
    return (
      <ThinBanner severity="warning">
        <WarningAmberIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
        Cannot deploy — {blockedCount} requirement(s) needed:
        {hasConfigIssue && props.onEdit && (
          <Typography
            component="span"
            sx={{
              color: "#60a5fa",
              cursor: "pointer",
              textDecoration: "underline",
              ml: 0.5,
              fontSize: 13,
              fontWeight: 600,
            }}
            onClick={props.onEdit}
          >
            {editLabel}
          </Typography>
        )}
      </ThinBanner>
    );
  }

  if (kind === "provisioning") {
    return (
      <ThinBanner severity="info">
        Provisioning in progress — check the Pipeline tab for progress.
      </ThinBanner>
    );
  }

  if (kind === "success") {
    return (
      <ThinBanner
        severity="success"
        action={
          <Button
            size="small"
            sx={{ color: "#10b981", textTransform: "none", minWidth: "auto" }}
            onClick={props.onDismissDeployMessage}
          >
            ×
          </Button>
        }
      >
        {props.deployMessage}
      </ThinBanner>
    );
  }

  if (kind === "ciInfo") {
    return (
      <ThinBanner severity="info">
        {props.isCiManaged
          ? "CI-Managed Pipeline — the CI pipeline controls the full build and deploy cycle. Manual deploy is disabled."
          : "CI Build → Rusha Deploy — CI builds images, then automatically triggers Rusha deploy."}
      </ThinBanner>
    );
  }

  if (kind === "adoptedInfo") {
    return (
      <ThinBanner
        severity="info"
        action={
          <Button
            size="small"
            sx={{ color: "#60a5fa", textTransform: "none", minWidth: "auto" }}
            onClick={props.onEdit}
          >
            Review detected services →
          </Button>
        }
      >
        Adopted repo — review detected services before first deploy.
      </ThinBanner>
    );
  }

  return null;
}

export default StackStatusBar;
